import { datadogRum } from '@datadog/browser-rum'
import { CURRENT_VERSION, VERSION_JSON_URL } from './appVersion'
import { IS_PROD_EU } from '../constants/apis'

const isProd = import.meta.env.VITE_DATADOG_ENV === 'prod'

// URLs that are not user-initiated. A timer-driven request in a visible-but-idle
// tab would revive the expired RUM session every interval — inflating session
// counts (and billing) with nobody behind it. Keep them out of RUM entirely.
const NON_USER_ACTIVITY_URLS = [VERSION_JSON_URL]

const isNonUserActivity = (url: string): boolean =>
  NON_USER_ACTIVITY_URLS.some((match) => url.includes(match))

// Only true once datadogRum.init() has actually run — guards setUser/addAction
// against being called when RUM bailed out (non-prod, or missing token/appId).
let initialized = false

export function initRum() {
  // RUM ships to US Datadog, so EU replays, URLs and user identity stay out of it
  if (!isProd || IS_PROD_EU) {
    return
  }

  const clientToken = import.meta.env.VITE_DD_RUM_CLIENT_TOKEN
  const applicationId = import.meta.env.VITE_DD_RUM_APP_ID

  if (!clientToken || !applicationId) {
    return
  }

  datadogRum.init({
    clientToken,
    applicationId,
    site: 'datadoghq.com',
    service: 'backoffice-ui',
    env: 'prod',
    version: CURRENT_VERSION || 'unknown',
    sessionSampleRate: 100,
    sessionReplaySampleRate: 20,
    trackUserInteractions: true,
    trackResources: true,
    trackLongTasks: true,
    defaultPrivacyLevel: 'mask-user-input',
    propagateTraceBaggage: false,
    excludedActivityUrls: [isNonUserActivity],
    beforeSend: (event) =>
      !(event.type === 'resource' && isNonUserActivity(event.resource.url))
  })

  datadogRum.startSessionReplayRecording()
  initialized = true
}

export function setRumUser(email: string, name: string) {
  if (!initialized) {
    return
  }

  datadogRum.setUser({ id: email, email, name })
}

export function trackAction(name: string, context?: Record<string, unknown>) {
  if (!initialized) {
    return
  }

  datadogRum.addAction(name, context)
}
